<template>
  <div class="oauth-section">
    <!-- Divider with OR -->
    <div class="divider-wrapper">
      <div class="divider-line" />
      <span class="divider-text">OR</span>
      <div class="divider-line" />
    </div>

    <!-- OAuth Buttons (each requires client id + VUE_APP_OAUTH_REDIRECT_URI) -->
    <div class="oauth-buttons">
      <v-btn
        v-if="showGoogleOAuth"
        block
        depressed
        :ripple="false"
        class="oauth-button"
        :loading="loadingProviders.google"
        :disabled="isLoading"
        @click="loginWithGoogle"
        data-testid="google-oauth-button"
      >
        <google-icon class="oauth-icon" />
        <span class="oauth-button-text">Continue with Google</span>
      </v-btn>

      <v-btn
        v-if="showMicrosoftOAuth"
        block
        depressed
        :ripple="false"
        class="oauth-button"
        :loading="loadingProviders.microsoft"
        :disabled="isLoading"
        @click="loginWithMicrosoft"
        data-testid="microsoft-oauth-button"
      >
        <microsoft-icon class="oauth-icon" />
        <span class="oauth-button-text">Continue with Microsoft</span>
      </v-btn>
    </div>

    <!-- OAuth Error Alert -->
    <v-alert
      v-if="oauthError"
      type="error"
      dismissible
      @input="oauthError = null"
      class="mt-4"
      data-testid="oauth-error-alert"
    >
      {{ oauthError }}
    </v-alert>
  </div>
</template>

<script>
import {
  initializeOAuthLogin,
  isGoogleOAuthConfigured,
  isMicrosoftOAuthConfigured,
} from "@/services/oauthService";
import { readableReason } from "@/helpers/humanText";
import GoogleIcon from "./GoogleIcon";
import MicrosoftIcon from "./MicrosoftIcon";

export default {
  name: "OAuthSigninSection",
  components: {
    GoogleIcon,
    MicrosoftIcon,
  },
  data() {
    return {
      loadingProviders: {
        google: false,
        microsoft: false,
      },
      oauthError: null,
    };
  },
  computed: {
    isLoading() {
      return this.loadingProviders.google || this.loadingProviders.microsoft;
    },
    showGoogleOAuth() {
      return isGoogleOAuthConfigured();
    },
    showMicrosoftOAuth() {
      return isMicrosoftOAuthConfigured();
    },
  },
  methods: {
    async loginWithGoogle() {
      await this.handleOAuthLogin("google");
    },

    async loginWithMicrosoft() {
      await this.handleOAuthLogin("microsoft");
    },

    postLoginRedirectFromRoute() {
      const raw = this.$route.query.redirect;
      if (typeof raw === "string" && raw) {
        return raw;
      }
      if (Array.isArray(raw) && raw[0]) {
        return raw[0];
      }
      return "/";
    },

    async handleOAuthLogin(provider) {
      try {
        this.oauthError = null;
        this.loadingProviders[provider] = true;

        await initializeOAuthLogin(provider, this.postLoginRedirectFromRoute());
      } catch (error) {
        this.oauthError = readableReason(error.message, `Failed to login with ${provider}. Please try again.`);
        this.loadingProviders[provider] = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.oauth-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.divider-wrapper {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  margin: 12px 0;

  .divider-line {
    flex: 1;
    height: 1px;
    background-color: rgba(17, 16, 16, 0.08);
  }

  .divider-text {
    font-family: "Outfit", sans-serif;
    font-size: 12px;
    font-weight: 400;
    color: #a09f9f;
    letter-spacing: 0.12px;
    line-height: 16px;
    white-space: nowrap;
  }
}

.oauth-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

/* Matches WelcomeScreen.tsx SSOButton: flat border, no shadow, subtle hover */
.oauth-button {
  background-color: #ffffff !important;
  border: 1px solid rgba(17, 16, 16, 0.08) !important;
  color: #111010 !important;
  font-family: "Outfit", sans-serif;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.12px;
  line-height: 16px;
  text-transform: none !important;
  min-height: 40px !important;
  height: auto !important;
  padding: 12px 24px !important;
  border-radius: 4px !important;
  box-shadow: none !important;
  /* WelcomeScreen: transition-colors on the button only */
  transition: background-color 0.15s ease, border-color 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  /* Vuetify stacks a dark ::before on hover; disable so hover matches WelcomeScreen rgba(17,16,16,0.02) only */
  &::before {
    display: none !important;
  }

  &:hover:not(:disabled) {
    background-color: rgba(17, 16, 16, 0.02) !important;
    border-color: rgba(17, 16, 16, 0.08) !important;
    box-shadow: none !important;
  }

  &:focus,
  &:active,
  &.v-btn--active {
    box-shadow: none !important;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    box-shadow: none !important;
  }

  .oauth-icon {
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .oauth-button-text {
    font-weight: 700;
    font-size: 12px;
    line-height: 16px;
  }

  ::v-deep .v-btn__content {
    gap: 12px;
    font-weight: 700;
  }

  ::v-deep .v-progress-circular {
    width: 20px !important;
    height: 20px !important;
    margin-right: 8px;

    svg {
      width: 20px !important;
      height: 20px !important;
    }
  }
}

// Mobile Responsive
@media (max-width: 600px) {
  .divider-wrapper {
    gap: 12px;
    margin: 8px 0;

    .divider-text {
      font-size: 11px;
    }
  }

  .oauth-buttons {
    gap: 8px;
  }

  .oauth-button {
    min-height: 40px !important;
    padding: 10px 20px !important;
    font-size: 11px;
    gap: 10px;

    .oauth-icon {
      width: 18px;
      height: 18px;
    }

    .oauth-button-text {
      font-size: 11px;
    }
  }
}

@media (max-width: 400px) {
  .oauth-buttons {
    gap: 6px;
  }

  .oauth-button {
    min-height: 36px !important;
    padding: 8px 16px !important;
    font-size: 10px;
    gap: 8px;

    .oauth-icon {
      width: 16px;
      height: 16px;
    }

    .oauth-button-text {
      font-size: 10px;
    }
  }
}
</style>
